import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  QrCodeScanner,
  Assessment,
  Warning,
  Inventory,
  Category as CategoryIcon,
  NotificationsOutlined,
  Person,
  AddCircleOutline,
  TableChart,
  History,
  AssignmentReturn,
  ManageAccounts,
  LockReset,
  Logout,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getGroupedProductsForStoreManager } from '../services/productService';
import { changeOwnPassword } from '../services/authService';

const drawerWidth = 260;

const routeTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/reports': 'Reports',
  '/login-history': 'Login History',
  '/users': 'User Management',
  '/low-stock': 'Low Stock Alert',
  '/products': 'Products',
  '/scan': 'Scan Barcode',
  '/categories': 'Categories',
  '/stock-in': 'Stock-In',
  '/stock-sheet': 'Stock Sheet',
  '/stock-return': 'Return Items'
};

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [lowStockCount, setLowStockCount] = useState(0);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwVisible, setPwVisible] = useState({ current: false, next: false, confirm: false });

  useEffect(() => {
    const loadLowStockCount = async () => {
      try {
        const grouped = await getGroupedProductsForStoreManager();
        const count = grouped.filter((g) => g.balance <= g.lowStockThreshold).length;
        setLowStockCount(count);
      } catch {
        setLowStockCount(0);
      }
    };
    loadLowStockCount();
  }, [location.pathname]);

  const adminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Reports', icon: <Assessment />, path: '/reports' },
    { text: 'Login History', icon: <History />, path: '/login-history' },
    { text: 'Users', icon: <ManageAccounts />, path: '/users' },
    { text: 'Low Stock', icon: <Warning />, path: '/low-stock' },
    { text: 'Products', icon: <Inventory />, path: '/products' }
  ];

  const storeManagerMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Reports', icon: <Assessment />, path: '/reports' },
    { text: 'Low Stock Alert', icon: <Warning />, path: '/low-stock' },
    { text: 'Stock Sheet', icon: <TableChart />, path: '/stock-sheet' },
    { text: 'Scan Barcode', icon: <QrCodeScanner />, path: '/scan' },
    { text: 'Stock-In', icon: <AddCircleOutline />, path: '/stock-in' },
    { text: 'Return Items', icon: <AssignmentReturn />, path: '/stock-return' },
    { text: 'Products', icon: <Inventory />, path: '/products' },
    { text: 'Categories', icon: <CategoryIcon />, path: '/categories' }
  ];

  const getMenuItems = () => {
    if (!user || !user.role) return [];
    return user.role === 'admin' ? adminMenuItems : storeManagerMenuItems;
  };

  const menuItems = getMenuItems();
  const pageTitle = routeTitles[location.pathname] || 'Dashboard';
  const isStoreManager = user?.role === 'store_manager';
  const sidebarColor = isStoreManager ? '#78121c' : '#db342c';

  const handleLogout = async () => {
    setMenuAnchor(null);
    await logout();
    navigate('/login');
  };

  const handleOpenPwDialog = () => {
    setMenuAnchor(null);
    setPwForm({ current: '', next: '', confirm: '' });
    setPwVisible({ current: false, next: false, confirm: false });
    setPwError('');
    setPwSuccess(false);
    setPwDialogOpen(true);
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (pwForm.next.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwError('New password and confirmation do not match.');
      return;
    }
    setPwSaving(true);
    try {
      await changeOwnPassword(pwForm.current, pwForm.next);
      setPwSuccess(true);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (e: unknown) {
      setPwError((e as Error).message || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Left sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: sidebarColor,
            borderRight: 'none',
            boxShadow: '2px 0 12px rgba(0,0,0,0.08)'
          }
        }}
      >
        <List sx={{ px: 1.5, pt: 2 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  color: location.pathname === item.path ? '#fff' : 'rgba(255,255,255,0.85)',
                  bgcolor: location.pathname === item.path ? 'rgba(255,255,255,0.18)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                  '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.22)' }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Right side: header + content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top header bar */}
        <Box
          sx={{
            height: 64,
            bgcolor: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontFamily: "'Poppins', 'Inter', sans-serif" }}>
            {pageTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="medium"
              sx={{ color: '#5f5f5f' }}
              aria-label={lowStockCount > 0 ? `${lowStockCount} low stock alerts` : 'Notifications'}
              onClick={() => lowStockCount > 0 && navigate('/low-stock')}
            >
              <Badge
                badgeContent={lowStockCount > 0 ? lowStockCount : 0}
                color="error"
                overlap="circular"
              >
                <NotificationsOutlined />
              </Badge>
            </IconButton>
            <Typography sx={{ fontSize: '0.875rem', color: '#5f5f5f', fontWeight: 500 }}>
              {user?.displayName}
            </Typography>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              size="small"
              aria-label="Account menu"
              sx={{ p: 0.5 }}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: sidebarColor }}>
                <Person sx={{ fontSize: 22, color: '#fff' }} />
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user?.displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleOpenPwDialog}>
                <ListItemIcon>
                  <LockReset fontSize="small" />
                </ListItemIcon>
                Change password
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Log out
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Main content area - light gray background */}
        <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: '#f0f2f5' }}>
          {children}
        </Box>
      </Box>

      {/* Change password dialog (available to all roles) */}
      <Dialog open={pwDialogOpen} onClose={() => setPwDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change password</DialogTitle>
        <DialogContent>
          {pwSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Your password has been updated.
            </Alert>
          ) : (
            <>
              {pwError && (
                <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                  {pwError}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Current password"
                type={pwVisible.current ? 'text' : 'password'}
                value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                margin="normal"
                autoComplete="current-password"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={() => setPwVisible((v) => ({ ...v, current: !v.current }))}
                        edge="end"
                        size="small"
                      >
                        {pwVisible.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                label="New password"
                type={pwVisible.next ? 'text' : 'password'}
                value={pwForm.next}
                onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                margin="normal"
                autoComplete="new-password"
                helperText="Minimum 8 characters"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setPwVisible((v) => ({ ...v, next: !v.next }))}
                        edge="end"
                        size="small"
                      >
                        {pwVisible.next ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                label="Confirm new password"
                type={pwVisible.confirm ? 'text' : 'password'}
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                margin="normal"
                autoComplete="new-password"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setPwVisible((v) => ({ ...v, confirm: !v.confirm }))}
                        edge="end"
                        size="small"
                      >
                        {pwVisible.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwDialogOpen(false)} sx={{ color: sidebarColor }}>
            {pwSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!pwSuccess && (
            <Button
              onClick={handleChangePassword}
              variant="contained"
              disabled={
                pwSaving ||
                !pwForm.current ||
                pwForm.next.length < 8 ||
                !pwForm.confirm
              }
              sx={{ bgcolor: sidebarColor, '&:hover': { bgcolor: sidebarColor, filter: 'brightness(0.9)' } }}
            >
              Update password
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
